// XXX: Motion doesn't read initial style state from the DOM so we need to specify to and from values
import { animate } from 'motion';

import { CUSTOM_EVENT_HIDEQUICKNAV } from '../site-header/elements';

import tokens from '@src/scss/global/exports.module.scss';

const DEFAULT_STEP_ANGLE = 10;
const TAB_MARGIN = 12;
const INITIAL_TRANSITION = {
  duration: 1,
  ease: [0.3, 0, 0, 1],
};
const MOVE_TRANSITION = {
  duration: 1,
  ease: [0.67, 0, 0.33, 1],
};
const RESET_TRANSITION = {
  duration: 1,
  ease: [1, 0, 0.7, 1],
};
const PANEL_MARGIN = 64;

const desktopMediaQuery = window.matchMedia(`(min-width: ${tokens.tabletBreakpoint})`);

const INITIALIZE_ANIMATION_STEPS = {
  footer: { translateY: [0, '12%'] },
  defaultBg: { filter: [`blur(0)`, `blur(10px)`] },
  mobileTabs: { transformY: [0, '12px'], opacity: [1, 0] },
  nextButton: {
    translateX: ['-50%', '11.4%'],
    translateY: ['-50%', '-50%'],
  },
  prevButton: { translateX: ['-50%', '-111.4%'], translateY: ['-50%', '-50%'] },
  text: { opacity: [1, 1], translateY: [0, 0] },
  images: { translateX: [0, 0] },
  header: { translateY: ['0', '-12px'], opacity: [1, 0] },
  bg: { translateX: ['100%', 0] },
  panelNodeDesktop: {
    bottom: ['56px', '24px'],
  },
  panelNodeMobile: {
    // Reset possible desktop styles
    rotate: [0, 0],
  },
  panelBodyOpacity: { opacity: [0, 1] },
  panelBodyDesktop: {
    translateX: ['-50%', '-50%'],
  },
  panelBodyMobile: {
    translateX: ['-50%', '-50%'],
    translateY: ['12px', 0],
    // Reset possible desktop styles
    scaleX: [1, 1],
    scaleY: [1, 1],
  },
  closeButton: { translateX: ['-50%', '-50%'], translateY: ['-50%', '-50%'], scale: [0, 1] },
};

const RESET_ANIMATION_STEPS = reverseAnimationSteps(INITIALIZE_ANIMATION_STEPS);

function reverseAnimationSteps(steps) {
  const reversed = {};
  for (const key in steps) {
    reversed[key] = {};
    for (const prop in steps[key]) {
      reversed[key][prop] = steps[key][prop].slice().reverse();
    }
  }
  return reversed;
}

function radiansToDegrees(radians) {
  return radians * (180 / Math.PI);
}

class Puzzle extends HTMLElement {
  constructor() {
    super();

    this.nextButtonNode = this.querySelector('[data-ref="next"]');
    this.prevButtonNode = this.querySelector('[data-ref="prev"]');
    this.headerNode = this.querySelector('[data-ref="header"]');
    this.desktopTabNodes = this.querySelectorAll('[data-ref="desktop-tab"]');
    this.bgNodes = this.querySelectorAll('[data-ref="bg"]');
    this.panelNode = this.querySelector('[data-ref="panel"]');
    this.panelBodyNode = this.querySelector('[data-ref="panel-body"]');
    this.imageNodes = this.querySelectorAll('[data-ref="image"]');
    this.textNodes = this.querySelectorAll('[data-ref="text"]');
    this.footerNode = this.querySelector('[data-ref="footer"]');
    this.defaultBgNode = this.querySelector('[data-ref="default-bg"]');
    this.mobileTabsNode = this.querySelector('[data-ref="mobile-tabs"]');
    this.closeButtonNode = this.querySelector('[data-ref="close"]');
    this.desktopTabsNode = this.querySelector('[data-ref="desktop-tabs"]');

    this.initialIndex = Math.floor(this.desktopTabNodes.length / 2);
    this.index = this.initialIndex;
    this.initialized = false;
    this.animation = null;
    this.animating = false;
    this.stepAngle = DEFAULT_STEP_ANGLE;
    this.focusedIndex = 0;

    this.nextButtonNode.addEventListener('click', this._handleClickNext);
    this.prevButtonNode.addEventListener('click', this._handleClickPrev);
    this.closeButtonNode.addEventListener('click', this._handleClickClose);

    this.addEventListener('click', this._handleClickBody);

    this.desktopTabsNode.addEventListener('keydown', this._handleKeyDownTabs);
    this.mobileTabsNode.addEventListener('keydown', this._handleKeyDownTabs);

    this.desktopTabsNode.addEventListener('focusout', this._handleFocusOutTabs);
    this.mobileTabsNode.addEventListener('focusout', this._handleFocusOutTabs);

    this.resizeObserver = new ResizeObserver(() => {
      this._handleResize();
    });
    this.resizeObserver.observe(this);

    this._updateAngles();
  }

  _handleClickNext = () => {
    if (!this.initialized) {
      this._initialize();
    } else {
      this._move(this.index + 1);
    }
  };

  _handleClickPrev = () => {
    this._move(this.index - 1);
  };

  _handleClickBody = (event) => {
    const tabNode = event.target.closest('[data-ref$="tab"]');

    if (!tabNode) {
      return;
    }

    const index = parseInt(tabNode.dataset.index, 10);

    if (!this.initialized) {
      this._initialize(index);
    } else {
      this._move(index);
    }
  };

  _handleResize = () => {
    if (this.animating) {
      this.animation.complete();
    }
    this._updateAngles();
  };

  _handleClickClose = () => {
    this._reset();
  };

  _handleKeyDownTabs = (event) => {
    const desktop = event.currentTarget === this.desktopTabsNode;

    switch (event.key) {
      case 'ArrowLeft':
        this._moveFocus(-1, desktop);
        break;
      case 'ArrowRight':
        this._moveFocus(+1, desktop);
        break;
    }
  };

  _handleFocusOutTabs = (event) => {
    if (!this.initialized) {
      const newIndex = 0;
      this._updateTabIndexes(newIndex);
      this.focusedIndex = newIndex;
    }
  };

  _moveFocus(d, desktop) {
    let newIndex = this.focusedIndex + d;
    if (newIndex < 0) {
      newIndex = this.desktopTabNodes.length - 1;
    } else if (newIndex >= this.desktopTabNodes.length) {
      newIndex = 0;
    }

    this._updateTabIndexes(newIndex);

    if (desktop) {
      this.desktopTabNodes[newIndex].firstElementChild.focus({ preventScroll: true });
    } else {
      Array.from(this.mobileTabsNode.children)[newIndex].firstElementChild.focus({
        preventScroll: true,
      });
    }

    this.focusedIndex = newIndex;
  }

  _updateTabIndexes(activeIndex = 0) {
    this.desktopTabNodes.forEach((node, i) => {
      node.firstElementChild.tabIndex = i === activeIndex ? null : '-1';
    });
    Array.from(this.mobileTabsNode.children).forEach((node, i) => {
      node.firstElementChild.tabIndex = i === activeIndex ? null : '-1';
    });
  }

  async _initialize(index = 0) {
    if (this.animating) {
      return;
    }

    const tabDimensions = this._getTabDimensions();
    const panelDimensions = this._getPanelDimensions();
    const panelOffsetAngle = this._calculatePanelOffsetAngle(tabDimensions, panelDimensions);

    this.panelOffsetAngle = panelOffsetAngle;

    const sequence = [];

    sequence.push(
      'k0',
      [this.footerNode, INITIALIZE_ANIMATION_STEPS.footer, { at: 'k0' }],
      [this.defaultBgNode.firstElementChild, INITIALIZE_ANIMATION_STEPS.defaultBg, { at: 'k0' }],
      [this.mobileTabsNode, INITIALIZE_ANIMATION_STEPS.mobileTabs, { at: 'k0' }],
      'k1',
      [
        this.nextButtonNode.parentNode,
        {
          ...INITIALIZE_ANIMATION_STEPS.nextButton,
          rotate: desktopMediaQuery.matches ? [0, 0] : ['-90deg', 0],
        },
        { at: 'k1' },
      ],
      [this.prevButtonNode.parentNode, INITIALIZE_ANIMATION_STEPS.prevButton, { at: 'k1' }],
      [this.closeButtonNode, INITIALIZE_ANIMATION_STEPS.closeButton, { at: 'k1' }]
    );

    this.desktopTabNodes.forEach((node, i) => {
      sequence.push([
        node,
        { rotate: [this._getTabAngle(i, this.index), this._getTabAngle(i, index, true)] },
        { at: 'k1' },
      ]);
    });

    // Null animations to set panel contents initial state
    sequence.push(
      [Array.from(this.textNodes[index].children), INITIALIZE_ANIMATION_STEPS.text, { at: 'k1' }],
      [this.imageNodes[index], INITIALIZE_ANIMATION_STEPS.images, { at: 'k1' }]
    );

    sequence.push(
      [
        this.headerNode,
        INITIALIZE_ANIMATION_STEPS.header,
        { at: 'k1', duration: INITIAL_TRANSITION.duration / 2 },
      ],
      [this.bgNodes[index], INITIALIZE_ANIMATION_STEPS.bg, { at: 'k1' }]
    );

    if (desktopMediaQuery.matches) {
      sequence.push(
        [
          this.panelBodyNode,
          {
            ...INITIALIZE_ANIMATION_STEPS.panelBodyDesktop,
            scaleX: [tabDimensions.width / panelDimensions.width, 1],
            scaleY: [tabDimensions.height / panelDimensions.height, 1],
          },
          { at: 'k1' },
        ],
        [
          this.panelBodyNode,
          INITIALIZE_ANIMATION_STEPS.panelBodyOpacity,
          { at: 'k1', duration: INITIAL_TRANSITION.duration / 4 },
        ],
        [
          this.panelNode,
          {
            ...INITIALIZE_ANIMATION_STEPS.panelNodeDesktop,
            rotate: [this._getTabAngle(index, this.index), this._getTabAngle(index, index, true)],
          },
          { at: 'k1' },
        ]
      );
    } else {
      sequence.push(
        [this.panelBodyNode, INITIALIZE_ANIMATION_STEPS.panelBodyMobile, { at: 'k1' }],
        [
          this.panelBodyNode,
          INITIALIZE_ANIMATION_STEPS.panelBodyOpacity,
          { at: 'k1', duration: INITIAL_TRANSITION.duration / 4 },
        ],
        [this.panelNode, INITIALIZE_ANIMATION_STEPS.panelNodeMobile, , { at: 'k1' }]
      );
    }

    this._updateTabState(index);

    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_HIDEQUICKNAV));

    this.animating = true;
    this.animation = animate(sequence, { defaultTransition: INITIAL_TRANSITION });
    await this.animation;
    this.animating = false;

    this.index = index;
    this.initialized = true;
    this.dataset.initialized = 'true';
    this.mobileTabsNode.inert = true;

    this._updateButtonState();
  }

  async _reset() {
    if (!this.initialized || this.animating) {
      return;
    }

    const tabDimensions = this._getTabDimensions();
    const panelDimensions = this._getPanelDimensions();

    const sequence = [];

    sequence.push('k0');

    if (desktopMediaQuery.matches) {
      sequence.push(
        [
          this.panelBodyNode,
          {
            ...RESET_ANIMATION_STEPS.panelBodyDesktop,
            scaleX: [1, tabDimensions.width / panelDimensions.width],
            scaleY: [1, tabDimensions.height / panelDimensions.height],
          },
          { at: 'k0' },
        ],
        [
          this.panelBodyNode,
          RESET_ANIMATION_STEPS.panelBodyOpacity,
          {
            at: 'k0',
            duration: RESET_TRANSITION.duration / 4,
            delay: RESET_TRANSITION.duration * (3 / 4),
          },
        ],
        [
          this.panelNode,
          {
            ...RESET_ANIMATION_STEPS.panelNodeDesktop,
            rotate: [
              this._getTabAngle(this.index, this.index, true),
              this._getTabAngle(this.index, this.initialIndex, false),
            ],
          },
          { at: 'k0' },
        ]
      );
    } else {
      sequence.push(
        [this.panelBodyNode, RESET_ANIMATION_STEPS.panelBodyMobile, { at: 'k0' }],
        [
          this.panelBodyNode,
          RESET_ANIMATION_STEPS.panelBodyOpacity,
          {
            at: 'k0',
            // duration: RESET_TRANSITION.duration / 4,
            // delay: RESET_TRANSITION.duration * (3 / 4),
          },
        ]
      );
    }

    sequence.push(
      [
        this.headerNode,
        RESET_ANIMATION_STEPS.header,
        {
          at: 'k0',
          duration: RESET_TRANSITION.duration / 2,
          delay: RESET_TRANSITION.duration / 2,
        },
      ],
      [this.bgNodes[this.index], RESET_ANIMATION_STEPS.bg, { at: 'k0' }]
    );

    this.desktopTabNodes.forEach((node, i) => {
      sequence.push([
        node,
        {
          rotate: [
            this._getTabAngle(i, this.index),
            this._getTabAngle(i, this.initialIndex, false),
          ],
          opacity: [i === this.index ? 0 : 1, 1],
        },
        { at: 'k0' },
      ]);
    });

    sequence.push(
      [
        this.nextButtonNode.parentNode,
        {
          ...RESET_ANIMATION_STEPS.nextButton,
          rotate: desktopMediaQuery.matches ? [0, 0] : [0, '-90deg'],
        },
        { ...INITIAL_TRANSITION, at: 'k0' },
      ],
      [
        this.prevButtonNode.parentNode,
        RESET_ANIMATION_STEPS.prevButton,
        { ...INITIAL_TRANSITION, at: 'k0' },
      ],
      [this.closeButtonNode, RESET_ANIMATION_STEPS.closeButton, { at: 'k0' }]
    );

    sequence.push(
      'k1',
      [this.footerNode, RESET_ANIMATION_STEPS.footer, { ...INITIAL_TRANSITION, at: 'k1' }],
      [this.defaultBgNode.firstElementChild, RESET_ANIMATION_STEPS.defaultBg, { at: 'k1' }],
      [this.mobileTabsNode, RESET_ANIMATION_STEPS.mobileTabs, { at: 'k1' }]
    );

    sequence.push(
      'k2',
      [[...this.textNodes[this.index].children], { opacity: [1, 0] }, { at: 'k2' }],
      [this.imageNodes[this.index], { translateX: [0, '100%'] }, { at: 'k2' }]
    );

    this.animating = true;
    this.animation = animate(sequence, { defaultTransition: RESET_TRANSITION });
    await this.animation;
    this.animating = false;

    this.index = this.initialIndex;
    this.initialized = false;
    this.dataset.initialized = 'false';
    this.mobileTabsNode.inert = false;

    this._resetButtonState();

    this._updateTabIndexes();
    this.focusedIndex = 0;
  }

  async _move(index) {
    if (this.animating) {
      return;
    }

    const oldIndex = this.index;
    const newIndex = index;
    const d = newIndex - oldIndex;

    if (newIndex < 0 || newIndex >= this.desktopTabNodes.length) {
      return;
    }

    const sequence = [];

    sequence.push('k0');

    this.desktopTabNodes.forEach((node, i) => {
      sequence.push(
        [
          node,
          { rotate: [this._getTabAngle(i, oldIndex), this._getTabAngle(i, newIndex)] },
          { at: 'k0' },
        ],
        [
          node,
          { opacity: [i === oldIndex ? 0 : 1, i === newIndex ? 0 : 1] },
          {
            at: 'k0',
            duration: MOVE_TRANSITION.duration / 2,
            delay: i === oldIndex ? MOVE_TRANSITION.duration / 2 : 0,
          },
        ]
      );
    });

    sequence.push(
      [
        [...this.textNodes[oldIndex].children],
        { translateY: [0, '12px'], opacity: [1, 0] },
        { duration: MOVE_TRANSITION.duration / 2, at: 'k0' },
      ],
      [
        [...this.textNodes[newIndex].children],
        { translateY: [0, 0], opacity: [0, 1] },
        { duration: MOVE_TRANSITION.duration / 2, at: 'k0', delay: MOVE_TRANSITION.duration / 2 },
      ],
      [this.imageNodes[oldIndex], { translateX: d > 0 ? [0, '-100%'] : [0, '100%'] }, { at: 'k0' }],
      [this.imageNodes[newIndex], { translateX: d > 0 ? ['100%', 0] : ['-100%', 0] }, { at: 'k0' }],
      [this.bgNodes[oldIndex], { translateX: d > 0 ? [0, '100%'] : [0, '-100%'] }, { at: 'k0' }],
      [this.bgNodes[newIndex], { translateX: d > 0 ? ['-100%', 0] : ['100%', 0] }, { at: 'k0' }]
    );

    this._updateTabState(newIndex);

    this.animating = true;
    this.animation = animate(sequence, { defaultTransition: MOVE_TRANSITION });
    await this.animation;
    this.animating = false;

    this.index = newIndex;

    this._updateButtonState();
  }

  _updateButtonState() {
    this.nextButtonNode.disabled = this.index === this.desktopTabNodes.length - 1;
    this.prevButtonNode.disabled = this.index === 0;
    this.closeButtonNode.disabled = !this.initialized;
  }

  _resetButtonState() {
    this.nextButtonNode.disabled = false;
    this.prevButtonNode.disabled = true;
    this.closeButtonNode.disabled = true;

    this.nextButtonNode.focus({ preventScroll: true });
  }

  _updateTabState(index) {
    this.desktopTabNodes.forEach((node) => {
      const i = parseInt(node.dataset.index, 10);
      const selected = i === index;
      node.querySelector('button').ariaSelected = selected;
      this.textNodes[i].ariaHidden = !selected;
      if (selected) {
        this.textNodes[i].removeAttribute('inert');
      } else {
        this.textNodes[i].setAttribute('inert', true);
      }
    });
  }

  _updateAngles() {
    const tabDimensions = this._getTabDimensions();
    const panelDimensions = this._getPanelDimensions();

    this.stepAngle = this._calculateStepAngle(tabDimensions);
    this.panelOffsetAngle = this._calculatePanelOffsetAngle(tabDimensions, panelDimensions);

    this.desktopTabNodes.forEach((node, i) => {
      node.style.transform = `rotate(${this._getTabAngle(i)}deg)`;
    });
  }

  _getTabAngle(i, index, initialized) {
    const effectiveIndex = index !== undefined ? index : this.index;
    const effectiveInitialized = initialized !== undefined ? initialized : this.initialized;
    const half = Math.floor(this.desktopTabNodes.length / 2);
    const currentAngle = -half * this.stepAngle + (half - effectiveIndex) * this.stepAngle;
    const offsetAngle = this.panelOffsetAngle - this.stepAngle;

    if (!effectiveInitialized) {
      return currentAngle + i * this.stepAngle;
    } else {
      return i === effectiveIndex
        ? currentAngle + i * this.stepAngle
        : i < effectiveIndex
          ? currentAngle + i * this.stepAngle - offsetAngle
          : currentAngle + i * this.stepAngle + offsetAngle;
    }
  }

  _calculateStepAngle(tabRect) {
    const circleRadius = this._getCircleRadius();
    const tabOffset = tabRect.width + TAB_MARGIN * 2;
    const stepAngle = Math.atan(tabOffset / circleRadius);
    const stepAngleDeg = radiansToDegrees(stepAngle);
    return stepAngleDeg;
  }

  _calculatePanelOffsetAngle(tabRect, panelRect) {
    const circleRadius = this._getCircleRadius();
    const panelOffset = panelRect.width / 2 + tabRect.width / 2 + PANEL_MARGIN;
    const panelOffsetAngle = Math.atan(panelOffset / circleRadius);
    const panelOffsetAngleDeg = radiansToDegrees(panelOffsetAngle);
    return panelOffsetAngleDeg;
  }

  _getCircleRadius() {
    return parseFloat(
      window
        .getComputedStyle(this.desktopTabNodes[0])
        .getPropertyValue('transform-origin')
        .split(' ')[1]
    );
  }

  _getTabDimensions() {
    return {
      // Get dimensions without transform
      width: this.desktopTabNodes[0].firstElementChild.offsetWidth,
      height: this.desktopTabNodes[0].firstElementChild.offsetHeight,
    };
  }

  _getPanelDimensions() {
    return {
      // Get dimensions without transform
      width: this.panelBodyNode.offsetWidth,
      height: this.panelBodyNode.offsetHeight,
    };
  }
}

window.customElements.define('cla-puzzle', Puzzle);
